import { ArtifactLink } from '../../components/ArtifactLink';
import { ITEM_QUALITY_NAMES, type ItemData, type ItemSourceView } from '@thottbot/shared';
import { FieldTable, Money, prose, refList } from './FieldTable';

const SOURCE_KIND_LABELS: Record<ItemSourceView['kind'], string> = {
  quest: 'Quest',
  dungeon: 'Dungeon',
  raid: 'Raid',
  world: 'World drop',
  vendor: 'Vendor',
  crafted: 'Crafted',
  pvp: 'PvP',
};

/** "Raid: Ragnaros (Molten Core), Vendor: ..." with links where the source has a page. */
function SourceList({ sources }: { sources: ItemSourceView[] }) {
  return (
    <>
      {sources.map((s, i) => (
        <span key={`${s.kind}-${s.slug ?? s.name}`} data-testid="item-source" data-kind={s.kind}>
          {i > 0 && ', '}
          {SOURCE_KIND_LABELS[s.kind]}:{' '}
          {s.slug && s.type ? <ArtifactLink type={s.type} slug={s.slug} name={s.name} /> : s.name}
          {s.zone && <span className="muted"> ({s.zone})</span>}
        </span>
      ))}
    </>
  );
}

export function ItemFields({ data, sources }: { data: ItemData; sources?: ItemSourceView[] }) {
  return (
    <FieldTable
      rows={[
        {
          label: 'Quality',
          value: data.quality !== undefined && (
            <span className={`quality-${data.quality}`}>{ITEM_QUALITY_NAMES[data.quality]}</span>
          ),
        },
        { label: 'Type', value: [data.itemClass, data.subclass].filter(Boolean).join(' / ') },
        { label: 'Slot', value: data.slot },
        { label: 'Binds', value: data.bind && data.bind !== 'None' ? data.bind : undefined },
        { label: 'Item level', value: data.itemLevel },
        { label: 'Requires level', value: data.requiredLevel },
        { label: 'Classes', value: data.classes?.join(', ') },
        { label: 'Races', value: data.races?.join(', ') },
        {
          label: 'Requires profession',
          value: data.requiredProfession && `${data.requiredProfession.name} (${data.requiredProfession.skill})`,
        },
        { label: 'PvP rank', value: data.pvpRank },
        {
          label: 'Damage',
          value:
            data.damage &&
            `${data.damage.min} - ${data.damage.max} (speed ${data.damage.speed.toFixed(2)})`,
        },
        { label: 'Armor', value: data.armor },
        {
          label: 'Stats',
          value: data.stats?.map((s) => `${s.value > 0 ? '+' : ''}${s.value} ${s.name}`).join(', '),
        },
        { label: 'Use', value: prose(data.use?.text) },
        { label: 'Duration', value: data.use?.duration },
        { label: 'Cooldown', value: data.use?.cooldown },
        { label: 'Sells for', value: data.sellPrice ? <Money copper={data.sellPrice} /> : undefined },
        { label: 'Description', value: prose(data.description) },
        {
          label: 'Sources',
          value: sources ? sources.length ? <SourceList sources={sources} /> : <span className="muted">unknown</span> : undefined,
        },
        { label: 'Dropped by', value: refList(data.droppedBy, 'npc') },
        { label: 'Sold by', value: refList(data.soldBy, 'npc') },
      ]}
    />
  );
}
